import { Input, Stack } from '@chakra-ui/react'
import pick from 'lodash/pick'
import type { FC, FormEventHandler } from 'react'
import { useState } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { toaster } from '@app/components/ui/toaster'
import FormAlert from '@app/next/forms/formAlert'
import { CardHeader } from '@app/shared/layout'
import TermsAndConditionsToggle from '@app/shared/termsAndConditionsToggle'
import unpackErrors from '@app/utils/unpackErrors'
import { useCaptureEmailMutation } from '@graphql/queries'
import type { UserEmailCaptureInput } from '@graphql/types'

const UserForm: FC = () => {
  const [errors, setErrors] = useState<ReturnType<typeof unpackErrors>>({})
  const [loading, setLoading] = useState(false)
  const [, captureEmail] = useCaptureEmailMutation()

  const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const input = Object.fromEntries(formData.entries())
    const validFields = ['email', 'jobTitle']

    setLoading(true)
    try {
      const response = await captureEmail({
        input: pick(input, validFields) as UserEmailCaptureInput
      })

      if (response.error) {
        setErrors(unpackErrors([response.error]))
        return
      }

      const { errors: mutationErrors } = response.data.userEmailCapture

      if (mutationErrors?.length) {
        setErrors(unpackErrors(mutationErrors))
        return
      }

      toaster.create({
        title: 'Finalizing your account',
        type: 'success'
      })

      window.location.href = '/'
    } catch (resp) {
      setErrors(unpackErrors([resp]))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack gap="6">
      <CardHeader
        title="Almost there!"
        subtitle="Add your work email address and job title to finish setting up your account."
      />
      <form onSubmit={onSubmit} id="user-form">
        <Stack gap="5">
          <FormAlert description={errors?.global?.message} title="Failed to setup your account!" />

          <Field label="Email" id="email" invalid={!!errors?.email} required>
            <Input autoComplete="email" id="email" name="email" placeholder="Enter your email" />
          </Field>
          <Field label="Job title" id="jobTitle">
            <Input id="jobTitle" name="jobTitle" placeholder="Enter your job title" />
          </Field>
          <TermsAndConditionsToggle name="termsAndConditions" />
        </Stack>
      </form>
      <Stack gap={4}>
        <Button loading={loading} form="user-form" size="md" type="submit">
          Continue
        </Button>
      </Stack>
    </Stack>
  )
}

export default UserForm
